import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Lang = 'en' | 'es' | 'bn' | 'ur' | 'hi' | 'ar' | 'fr';

export const LANGUAGES: { code: Lang; name: string; english: string; dir: 'ltr' | 'rtl' }[] = [
  { code: 'en', name: 'English', english: 'English', dir: 'ltr' },
  { code: 'es', name: 'Español', english: 'Spanish', dir: 'ltr' },
  { code: 'fr', name: 'Français', english: 'French', dir: 'ltr' },
  { code: 'ar', name: 'العربية', english: 'Arabic', dir: 'rtl' },
  { code: 'ur', name: 'اردو', english: 'Urdu', dir: 'rtl' },
  { code: 'hi', name: 'हिन्दी', english: 'Hindi', dir: 'ltr' },
  { code: 'bn', name: 'বাংলা', english: 'Bengali', dir: 'ltr' },
];

const KEY = 'easylearning.language';
const CHOSEN = 'easylearning.language.chosen';
const loaders = import.meta.glob<{ default: Record<string, string> }>('./locales/*.json');

type Vars = Record<string, string | number>;

let currentLang: Lang = readStored();
let currentDict: Record<string, string> = {};

function readStored(): Lang {
  try {
    const v = localStorage.getItem(KEY);
    return LANGUAGES.some((l) => l.code === v) ? (v as Lang) : 'en';
  } catch {
    return 'en';
  }
}

function persist(lang: Lang) {
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    return;
  }
}

function fill(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

export function markChosenBeforeSignIn() {
  try {
    localStorage.setItem(CHOSEN, '1');
  } catch {
    return;
  }
}

export function takeChosenBeforeSignIn(): boolean {
  try {
    const chosen = localStorage.getItem(CHOSEN) === '1';
    localStorage.removeItem(CHOSEN);
    return chosen;
  } catch {
    return false;
  }
}

export function translate(text: string, vars?: Vars): string {
  return fill(currentDict[text] ?? text, vars);
}

async function load(lang: Lang): Promise<Record<string, string>> {
  if (lang === 'en') return {};
  const loader = loaders[`./locales/${lang}.json`];
  return loader ? (await loader()).default : {};
}

function applyDocument(lang: Lang) {
  const dir = LANGUAGES.find((l) => l.code === lang)?.dir ?? 'ltr';
  document.documentElement.lang = lang;
  document.documentElement.dir = dir;
}

type Api = { lang: Lang; setLang: (l: Lang) => void; t: (text: string, vars?: Vars) => string };

const Ctx = createContext<Api>({ lang: 'en', setLang: () => {}, t: translate });

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(currentLang);
  const [dict, setDict] = useState<Record<string, string>>({});
  const [ready, setReady] = useState(lang === 'en');

  useEffect(() => {
    let cancelled = false;
    void load(lang).then((d) => {
      if (cancelled) return;
      currentLang = lang;
      currentDict = d;
      applyDocument(lang);
      setDict(d);
      setReady(true);
    });
    return () => { cancelled = true; };
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    persist(l);
    setLangState(l);
  }, []);

  const t = useCallback((text: string, vars?: Vars) => fill(dict[text] ?? text, vars), [dict]);
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  if (!ready) return null;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useT() {
  return useContext(Ctx).t;
}

export function useLanguage() {
  return useContext(Ctx);
}
